import { BACKEND_BASE_URL } from "../config/env";
import { COURIER_LABELS } from "../constants/couriers";
import { ONGKIR_COURIERS } from "../constants/ongkirCouriers";
import { detectCourierWithBackend } from "./autoDetectResi";
import { mapTrackingCourierToBackend, normalizeTrackingCourierSlug } from "./courierMapping";

const SITE_ORIGIN = "https://cek-resi.net";

// Tracking slugs whose /ongkir/<code> page uses a different code.
const TRACKING_TO_ONGKIR_CODE = {
  pos: "posindonesia",
  ide: "idx",
  jnt_cargo: "jtcargo",
  sentralcargo: "sentral",
};

// Fresh provider lookups need a captcha, so agents are sent to the courier page.
export function getTrackingPageUrl(courier) {
  const slug = normalizeTrackingCourierSlug(courier);
  return slug && COURIER_LABELS[slug] ? `${SITE_ORIGIN}/kurir/${slug}` : `${SITE_ORIGIN}/kurir`;
}

export function getOngkirPageUrl(courier) {
  const slug = normalizeTrackingCourierSlug(courier);
  const code = TRACKING_TO_ONGKIR_CODE[slug] ?? slug;
  return code && ONGKIR_COURIERS.some((c) => c.code === code) ? `${SITE_ORIGIN}/ongkir/${code}` : `${SITE_ORIGIN}/ongkir`;
}

function textResult(payload) {
  return { content: [{ type: "text", text: JSON.stringify(payload, null, 2) }] };
}

/**
 * Lightweight WebMCP (Web Model Context Protocol - https://webmcp.dev/) Polyfill and Tool Registry.
 * Exposes document.modelContext and navigator.modelContext for client-side AI Browser Agents.
 */
class ModelContextRegistry {
  constructor() {
    this.tools = new Map();
  }

  registerTool(toolDefinition) {
    if (!toolDefinition || typeof toolDefinition !== "object") {
      throw new TypeError("WebMCP tool definition must be an object");
    }
    if (!toolDefinition.name || typeof toolDefinition.name !== "string") {
      throw new TypeError("WebMCP tool name is required");
    }
    if (typeof toolDefinition.execute !== "function") {
      throw new TypeError("WebMCP tool execute method must be a function");
    }

    const normalizedTool = {
      name: toolDefinition.name,
      description: toolDefinition.description || "",
      inputSchema: toolDefinition.inputSchema || { type: "object", properties: {} },
      execute: toolDefinition.execute,
    };

    this.tools.set(normalizedTool.name, normalizedTool);
    return normalizedTool;
  }

  unregisterTool(name) {
    return this.tools.delete(name);
  }

  getTools() {
    return Array.from(this.tools.values()).map((t) => ({
      name: t.name,
      description: t.description,
      inputSchema: t.inputSchema,
    }));
  }

  async executeTool(name, args = {}) {
    const tool = this.tools.get(name);
    if (!tool) {
      throw new Error(`WebMCP tool '${name}' is not registered`);
    }
    return tool.execute(args);
  }
}

/**
 * Initialize WebMCP polyfill on window.document.modelContext and navigator.modelContext.
 */
export function initializeWebMCPPolyfill() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  if (!document.modelContext) {
    const registry = new ModelContextRegistry();
    const modelContextInterface = {
      registerTool: (def) => registry.registerTool(def),
      unregisterTool: (name) => registry.unregisterTool(name),
      getTools: () => registry.getTools(),
      executeTool: (name, args) => registry.executeTool(name, args),
    };

    document.modelContext = modelContextInterface;
    if (typeof navigator !== "undefined" && !navigator.modelContext) {
      navigator.modelContext = modelContextInterface;
    }
  }

  return document.modelContext;
}

/**
 * Register core Cekresi tools on WebMCP
 */
export function registerCekresiWebMCPTools() {
  const mc = initializeWebMCPPolyfill();
  if (!mc) return null;

  // Tool 1: lacak_resi
  mc.registerTool({
    name: "lacak_resi",
    description: "Tampilkan status dan riwayat paket untuk nomor resi yang sudah pernah dicek di Cek-Resi.net. Jika belum ada data, arahkan pengguna ke tracking_page untuk melacak resi di website.",
    inputSchema: {
      type: "object",
      properties: {
        resi: { type: "string", description: "Nomor resi pengiriman yang akan dilacak" },
        courier: { type: "string", description: "Kode ekspedisi/kurir (opsional, misal: jne, jnt, sicepat, spx, pos, tiki, wahana, ninja, lion, ide)" },
      },
      required: ["resi"],
    },
    execute: async ({ resi, courier }) => {
      const resiTrimmed = (resi || "").trim();
      if (!resiTrimmed) {
        return textResult({ error: "Nomor resi tidak boleh kosong" });
      }

      let selectedCourier = normalizeTrackingCourierSlug(courier || "");
      if (!selectedCourier) {
        const detectionInfo = await detectCourierWithBackend(resiTrimmed);
        selectedCourier = detectionInfo?.suggestedCourier || "";
      }

      const trackingPage = getTrackingPageUrl(selectedCourier);
      const notAvailable = (message) =>
        textResult({
          status: "NOT_AVAILABLE",
          resi: resiTrimmed,
          courier: selectedCourier || null,
          message,
          tracking_page: trackingPage,
          source: SITE_ORIGIN,
        });

      if (!selectedCourier) {
        return notAvailable("Kurir tidak terdeteksi. Minta pengguna memilih kurir dan memasukkan nomor resi di tracking_page.");
      }

      try {
        // Cache-only read: without force/refresh the backend never calls a provider.
        const backendCourier = mapTrackingCourierToBackend(selectedCourier);
        const res = await fetch(`${BACKEND_BASE_URL}/tracking?courier=${encodeURIComponent(backendCourier)}&rid=${encodeURIComponent(resiTrimmed)}`);
        if (!res.ok) {
          return notAvailable("Belum ada data untuk resi ini. Minta pengguna melacak resi langsung di tracking_page.");
        }

        const data = await res.json();
        return textResult({
          status: data.status || "UNKNOWN",
          service: data.service || "-",
          courier: selectedCourier,
          resi: resiTrimmed,
          origin: data.origin || "-",
          destination: data.destination || "-",
          receiver: data.receiver || "-",
          history: (data.history || []).map((h) => ({
            date: h.date,
            description: h.desc,
          })),
          canonical_url: `${SITE_ORIGIN}/kurir/${encodeURIComponent(selectedCourier)}/${encodeURIComponent(resiTrimmed)}`,
          tracking_page: trackingPage,
          source: SITE_ORIGIN,
          copyright: "Data provided by Cek-Resi.net - Lacak Resi & Cek Ongkir Indonesia",
        });
      } catch {
        return notAvailable("Gagal menghubungi layanan tracking. Minta pengguna melacak resi langsung di tracking_page.");
      }
    },
  });

  // Tool 2: deteksi_kurir
  mc.registerTool({
    name: "deteksi_kurir",
    description: "Deteksi otomatis nama ekspedisi / kurir berdasarkan pola format nomor resi pengiriman.",
    inputSchema: {
      type: "object",
      properties: {
        resi: { type: "string", description: "Nomor resi yang ingin dideteksi ekspedisinya" },
      },
      required: ["resi"],
    },
    execute: async ({ resi }) => {
      const resiTrimmed = (resi || "").trim();
      if (!resiTrimmed) {
        return {
          content: [{ type: "text", text: JSON.stringify({ error: "Nomor resi wajib diisi" }) }],
        };
      }

      const result = await detectCourierWithBackend(resiTrimmed);
      const payload = {
        resi: resiTrimmed,
        suggested_courier: result.suggestedCourier || null,
        courier_label: result.label || null,
        confidence: result.confidence || "none",
        source_detection: result.source || "client_regex",
        canonical_url: "https://cek-resi.net/deteksi-otomatis",
        source: "https://cek-resi.net",
      };

      return {
        content: [{ type: "text", text: JSON.stringify(payload, null, 2) }],
      };
    },
  });

  // Tool 3: cek_ongkir
  mc.registerTool({
    name: "cek_ongkir",
    description: "Berikan link halaman cek ongkir Cek-Resi.net. Tarif tidak dihitung lewat tool ini; pengguna memilih lokasi asal, tujuan, dan berat di halaman tersebut.",
    inputSchema: {
      type: "object",
      properties: {
        courier: { type: "string", description: "Kode kurir (opsional, misal: jne, jnt, sicepat, pos). Tanpa kurir, link mengarah ke halaman perbandingan semua kurir." },
      },
    },
    execute: async ({ courier } = {}) => {
      const ongkirPage = getOngkirPageUrl(courier || "");
      return textResult({
        ongkir_page: ongkirPage,
        message: "Buka ongkir_page lalu isi lokasi asal, tujuan, dan berat paket untuk melihat tarif dan estimasi waktu kirim.",
        source: SITE_ORIGIN,
      });
    },
  });

  // Tool 4: cari_kodepos
  mc.registerTool({
    name: "cari_kodepos",
    description: "Cari data kode pos, kelurahan, kecamatan, dan kabupaten/kota di Indonesia.",
    inputSchema: {
      type: "object",
      properties: {
        query: { type: "string", description: "Nama kelurahan, kecamatan, kota, atau 5-digit angka kode pos" },
      },
      required: ["query"],
    },
    execute: async ({ query }) => {
      const qTrimmed = (query || "").trim();
      if (!qTrimmed) {
        return {
          content: [{ type: "text", text: JSON.stringify({ error: "Query pencarian wajib diisi" }) }],
        };
      }

      try {
        const isNumeric = /^\d{3,5}$/.test(qTrimmed);
        const endpoint = isNumeric
          ? `${BACKEND_BASE_URL}/kodepos/search?q=${encodeURIComponent(qTrimmed)}`
          : `${BACKEND_BASE_URL}/kelurahans?q=${encodeURIComponent(qTrimmed)}`;

        const res = await fetch(endpoint);
        if (!res.ok) {
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify({
                  query: qTrimmed,
                  results: [],
                  canonical_url: "https://cek-resi.net/kodepos-kelurahan",
                  source: "https://cek-resi.net",
                }),
              },
            ],
          };
        }

        const data = await res.json();
        const payload = {
          query: qTrimmed,
          results: data,
          canonical_url: "https://cek-resi.net/kodepos-kelurahan",
          source: "https://cek-resi.net",
        };

        return {
          content: [{ type: "text", text: JSON.stringify(payload, null, 2) }],
        };
      } catch (err) {
        return {
          content: [
            {
              type: "text",
              text: JSON.stringify({
                error: err.message || "Gagal memproses pencarian kode pos",
                canonical_url: "https://cek-resi.net/kodepos-kelurahan",
                source: "https://cek-resi.net",
              }),
            },
          ],
        };
      }
    },
  });

  return mc;
}
